/**
 * Dòng "Chuẩn bị x/3 — còn thiếu: …" của drawer Bank (đợt 260922 E2-P3) — logic THUẦN, tách khỏi antd để jest chạy được.
 *
 * Học `Prep 4/5 — still need: Characters` của TopView: nói thẳng còn THIẾU gì trước khi gen clip, thay vì bắt người
 * mở từng tab đếm. Phạm vi = thứ plan đang mở dùng (`BankUsage`), cùng luật với bộ lọc "Chỉ tập này".
 */
import { filterByEpisode } from './bankFilterUtils';
import { countFilledLabels } from './characterAppearanceUtils';

export type PrepKey = 'chars' | 'locs' | 'props';

export interface PrepGroup {
  key: PrepKey;
  /** Số mục trong phạm vi — số hiện cạnh tên tab. */
  total: number;
  /** Mỗi phần tử là một cụm chữ, vd `2 nhân vật chưa đủ 7 nhãn`. Rỗng = nhóm đã đủ. */
  missing: string[];
  done: boolean;
}

export interface BankPrep {
  groups: PrepGroup[];
  done: number;
  line: string;
}

interface CharLike {
  id: string; appearance?: object | null; kind?: string | null; casting?: string | null;
  seedancePreviewUrl?: string | null; canonicalRefUrl?: string | null;
}
interface LookLike { id: string; characterId: string; lookKey: string; canonicalRefUrl?: string | null }
interface RowLike { characterId: string; lookId?: string | null }
interface ImgLike { id: string; canonicalRefUrl?: string | null }
interface UsageLike {
  characterIds: string[];
  locationIds: string[];
  propIds: string[];
  filterable: { characters: boolean; locations: boolean; props: boolean };
}

/** Bộ chính của nhân vật: `default` nếu có, không thì bộ đầu tiên (cùng thứ tự `looksOf`). */
function mainLookOf<T extends LookLike>(characterId: string, looks: T[]): T | null {
  const mine = looks.filter((l) => l.characterId === characterId);
  return mine.find((l) => l.lookKey === 'default') || mine[0] || null;
}

/**
 * Bộ đồ plan đang dùng mà CHƯA có ảnh người mặc — nguồn chung cho dòng chuẩn bị và nút `Gen ảnh các bộ còn thiếu`.
 * Theo bảng gán cảnh; nhân vật không có dòng gán nào ⇒ bộ chính. Dòng trỏ bộ đã xoá không tính ở đây (đếm riêng).
 *
 * 260922 E3-P1/P4 — THỨ TỰ có nghĩa: MỌI bộ phụ dựng từ ảnh BỘ CHÍNH (E3-P4 — trước đó chỉ nhân vật không mặt), backend từ chối bộ
 * phụ khi bộ chính chưa ảnh ⇒ bộ chính luôn đứng trước, và được CHÈN vào dù cảnh không ai mặc nó.
 */
export function looksNeedingImage<T extends LookLike>(chars: CharLike[], looks: T[], sceneRows: RowLike[]): T[] {
  return looksNeedingImageDetail(chars, looks, sceneRows).looks;
}

/** Như `looksNeedingImage`, kèm id các bộ chính được CHÈN (cảnh không dùng) — câu xác nhận nói rõ tổng số ảnh sẽ gen (mỗi ảnh là tiền). */
export function looksNeedingImageDetail<T extends LookLike>(chars: CharLike[], looks: T[], sceneRows: RowLike[]): { looks: T[]; insertedMainIds: string[] } {
  const byId = new Map(looks.map((l) => [l.id, l]));
  const out: T[] = [];
  const inserted: string[] = [];
  const seen = new Set<string>();
  const push = (l: T) => { if (!seen.has(l.id)) { seen.add(l.id); out.push(l); } };
  for (const c of chars) {
    if (c.kind === 'CROWD') continue; // đám đông không có bộ đồ / ảnh người mặc (ảnh nhóm = Epic 3 P5)
    const rows = sceneRows.filter((r) => r.characterId === c.id);
    const ids = rows.map((r) => r.lookId).filter((id): id is string => !!id && byId.has(id));
    const main = mainLookOf(c.id, looks);
    const used = ids.length ? ids.map((id) => byId.get(id)!) : !rows.length && main ? [main] : [];
    const need = used.filter((l) => !l.canonicalRefUrl);
    if (!need.length) continue;
    if (main && !main.canonicalRefUrl) {
      if (!need.includes(main)) inserted.push(main.id);
      push(main);
    }
    need.forEach(push);
  }
  return { looks: out, insertedMainIds: inserted };
}

export function bankPrep(args: {
  chars: CharLike[];
  looks: LookLike[];
  sceneRows: RowLike[];
  locations: ImgLike[];
  props: ImgLike[];
  usage?: UsageLike | null;
  /** Số người trong event mà Bank chưa có hồ sơ (danh sách rà ngược, 260922 E1-P2). */
  castCandidates?: number;
}): BankPrep {
  const u = args.usage;
  const chars = filterByEpisode(args.chars, u?.characterIds, { on: true, filterable: !!u?.filterable.characters, emptyMeansAll: true });
  const locs = filterByEpisode(args.locations, u?.locationIds, { on: true, filterable: !!u?.filterable.locations, emptyMeansAll: true });
  const props = filterByEpisode(args.props, u?.propIds, { on: true, filterable: !!u?.filterable.props });
  const charIds = new Set(chars.map((c) => c.id));
  const rows = args.sceneRows.filter((r) => charIds.has(r.characterId));

  const charMissing: string[] = [];
  // 260922 E1-P2: đám đông không có MỘT khuôn mặt ⇒ không đòi 7 nhãn / bộ đồ; chỉ đòi mô tả nhóm.
  const people = chars.filter((c) => c.kind !== 'CROWD');
  const crowds = chars.filter((c) => c.kind === 'CROWD');
  if (args.castCandidates) charMissing.push(`${args.castCandidates} người trong kịch bản chưa có hồ sơ`);
  const thieuNhan = people.filter((c) => countFilledLabels((c.appearance || {}) as any) < 7).length;
  if (thieuNhan) charMissing.push(`${thieuNhan} nhân vật chưa đủ 7 nhãn`);
  const nhomTrong = crowds.filter((c) => !String(c.casting || '').trim()).length;
  if (nhomTrong) charMissing.push(`${nhomTrong} nhóm chưa có mô tả`);
  const khongBo = people.filter((c) => !args.looks.some((l) => l.characterId === c.id)).length;
  if (khongBo) charMissing.push(`${khongBo} nhân vật chưa có bộ đồ`);
  const thieuAnh = looksNeedingImage(chars, args.looks, rows).length;
  if (thieuAnh) charMissing.push(`${thieuAnh} bộ đồ chưa có ảnh`);
  const lookIds = new Set(args.looks.map((l) => l.id));
  const hong = rows.filter((r) => !!r.lookId && !lookIds.has(r.lookId)).length;
  if (hong) charMissing.push(`${hong} cảnh trỏ bộ đồ đã xoá`);

  const noImg = (xs: ImgLike[], what: string) => {
    const n = xs.filter((x) => !x.canonicalRefUrl).length;
    return n ? [`${n} ${what} chưa có ảnh`] : [];
  };
  const groups: PrepGroup[] = [
    { key: 'chars', total: chars.length, missing: charMissing, done: !charMissing.length },
    { key: 'locs', total: locs.length, missing: noImg(locs, 'bối cảnh'), done: !noImg(locs, 'bối cảnh').length },
    { key: 'props', total: props.length, missing: noImg(props, 'đạo cụ'), done: !noImg(props, 'đạo cụ').length },
  ];
  const done = groups.filter((g) => g.done).length;
  const all = groups.flatMap((g) => g.missing);
  return { groups, done, line: all.length ? `Chuẩn bị ${done}/3 — còn thiếu: ${all.join(' · ')}` : 'Chuẩn bị 3/3 — đủ' };
}

/**
 * Ảnh đưa lên thẻ nhân vật cho một bộ đồ — thẻ chỉ có MỘT ảnh (User 23/09). Ưu tiên ảnh NGƯỜI MẶC (mặt + bộ đồ);
 * bộ mới chỉ có ảnh ma-nơ-canh thì hiện nó kèm nhãn để người xem không tưởng đó là nhân vật.
 */
export function lookImageState(look: { canonicalRefUrl?: string | null; seedanceOutfitRefUrl?: string | null } | null | undefined): { url: string | null; tag: string | null } {
  if (look?.canonicalRefUrl) return { url: look.canonicalRefUrl, tag: null };
  if (look?.seedanceOutfitRefUrl) return { url: look.seedanceOutfitRefUrl, tag: 'ma-nơ-canh' };
  return { url: null, tag: null };
}

/**
 * 260930 E1-P1 — bố cục ảnh bộ đồ theo TỈ LỆ ảnh (không cần cột DB): ảnh 16:9 (~1,78) = 1 ô cận ~40% + 3 ô góc (261008 E1-P2; cũng là sheet 4 khung 260922) ⇒ `sheet4`; ảnh 2 khung 4:3 (~1,34, bộ đã gen ở 260930) ⇒ `sheet2`;
 * dọc / vuông / chưa tải ⇒ `single`. Ảnh tải lên tỉ lệ 1,15–1,6 (vd 3:2) bị coi là `sheet2` — chỉ lệch hình thu nhỏ (chấp nhận, doc Phase 1 Bước 6).
 */
export type LookImageLayout = 'sheet4' | 'sheet2' | 'single';
export function lookImageLayout(naturalWidth: number, naturalHeight: number): LookImageLayout {
  if (!(naturalWidth > 0) || !(naturalHeight > 0)) return 'single';
  const r = naturalWidth / naturalHeight;
  if (r >= 1.6) return 'sheet4';
  if (r >= 1.15) return 'sheet2';
  return 'single';
}
